import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { MessageCircle, X, Send, ArrowRight, Loader2 } from "lucide-react";
import { publicApi } from "../lib/api";
import cassieAvatar from "../assets/cassie-avatar.webp";
import { trackEvent } from "../lib/analytics";

const uid = () => Math.random().toString(36).slice(2) + Date.now().toString(36);
const GREETING = "Hi, I'm Cassie, CASS's AI assistant 👋\nI'm here to answer questions about bookkeeping, payroll, VAT, tax, Xero, business growth and anything else you'd like to know.\nIf I can't answer your question, I'll introduce you to the right member of our team.\nHow can I help you today?";
const WELCOME = { role: "bot", text: GREETING, lets_talk: false };

// Cassie writes site addresses as plain paths. Only these become links, so a
// stray slash in her prose can never turn into a dead one.
const LINKABLE = [
  "/pricing", "/contact", "/legal-cashiering", "/ask-cassie", "/services", "/faqs",
  "/guides", "/more-than-numbers", "/meeting-room-hire", "/insights", "/testimonials",
  "/reviews", "/careers", "/blog", "/podcast", "/success-stories", "/about",
];
const PATH_RE = new RegExp(`(${LINKABLE.join("|")})(/[a-z0-9-]+)*`, "g");

const BotText = ({ text, onFollow }) => {
  const src = String(text || "");
  const out = [];
  let last = 0;
  for (const m of src.matchAll(PATH_RE)) {
    if (m.index > last) out.push(src.slice(last, m.index));
    out.push(
      <Link
        key={m.index}
        to={m[0]}
        onClick={() => onFollow(m[0])}
        data-testid={`chat-link${m[0].replace(/\//g, "-")}`}
        className="font-semibold text-orange hover:text-orange-dark underline decoration-orange/40"
      >
        {m[0]}
      </Link>
    );
    last = m.index + m[0].length;
  }
  out.push(src.slice(last));
  return <p className="whitespace-pre-wrap">{out}</p>;
};

// Map the current path to an editable prompt-group key (matching stays in code; content is CMS-editable).
const PAGE_KEY_RULES = [
  { key: "legal-cashiering", match: (p) => p.startsWith("/legal-cashiering") },
  { key: "pricing", match: (p) => p.startsWith("/pricing") },
  { key: "services", match: (p) => p.startsWith("/services") },
  { key: "about", match: (p) => p.startsWith("/about") },
  { key: "meeting-room", match: (p) => p.startsWith("/meeting-room") },
  { key: "business-health-check", match: (p) => p.startsWith("/business-health-check") },
  { key: "podcast", match: (p) => p.startsWith("/podcast") },
  { key: "insights", match: (p) => p.startsWith("/blog") || p.startsWith("/insights") },
  { key: "contact", match: (p) => p.startsWith("/contact") },
  { key: "home", match: (p) => p === "/" },
];
const pageKeyFor = (path) => PAGE_KEY_RULES.find((r) => r.match(path || "/"))?.key || null;

export default function ChatWidget() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [nudge, setNudge] = useState(false);
  const [messages, setMessages] = useState([WELCOME]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [promptCfg, setPromptCfg] = useState(null);
  const sessionRef = useRef(null);
  const scrollRef = useRef(null);
  const isLC = location.pathname.startsWith("/legal-cashiering");
  const pageKey = pageKeyFor(location.pathname);
  const suggestions = (promptCfg?.pages?.[pageKey]) || promptCfg?.default || [];
  const suggestionsHeading = promptCfg?.heading || "Popular questions";

  useEffect(() => { publicApi.cassiePrompts().then(setPromptCfg).catch(() => {}); }, []);

  // Apply the configured greeting to the opening message (only before any interaction).
  useEffect(() => {
    if (promptCfg?.greeting) {
      setMessages((m) => (m.length === 1 && m[0].role === "bot" ? [{ ...m[0], text: promptCfg.greeting }] : m));
    }
  }, [promptCfg]);

  useEffect(() => {
    let sid = localStorage.getItem("cass_chat_session");
    if (!sid) { sid = uid(); localStorage.setItem("cass_chat_session", sid); }
    sessionRef.current = sid;
  }, []);

  // Proactive nudge: show a friendly teaser once per session. On desktop after
  // a few seconds; on mobile only after the user scrolls past the hero, so it
  // never covers an above-the-fold call-to-action.
  useEffect(() => {
    if (sessionStorage.getItem("cass_chat_nudged")) return;
    const reveal = () => {
      if (!open) { setNudge(true); sessionStorage.setItem("cass_chat_nudged", "1"); }
    };
    const isMobile = window.matchMedia && window.matchMedia("(max-width: 767px)").matches;
    if (isMobile) {
      const onScroll = () => {
        if (window.scrollY > 500) { window.removeEventListener("scroll", onScroll); reveal(); }
      };
      window.addEventListener("scroll", onScroll, { passive: true });
      return () => window.removeEventListener("scroll", onScroll);
    }
    const t = setTimeout(reveal, 6000);
    return () => clearTimeout(t);
  }, [open]);

  // Engagement, not a lead: a chat being opened says someone wanted an answer.
  const openChat = (source = "widget_button") => {
    setOpen(true);
    setNudge(false);
    trackEvent("ask_cassie_opened", { page_path: window.location.pathname, opened_from: source });
  };

  // Hand-off from Ask Cassie: the question the visitor typed comes across with
  // them, ready to send, so nothing is retyped.
  useEffect(() => {
    const onAsk = (e) => {
      setOpen(true);
      setNudge(false);
      trackEvent("ask_cassie_opened", { page_path: window.location.pathname, opened_from: "ask_cassie_page" });
      setInput(String(e?.detail?.question || ""));
    };
    window.addEventListener("cass:ask-cassie", onAsk);
    return () => window.removeEventListener("cass:ask-cassie", onAsk);
  }, []);
  const dismissNudge = (e) => { e?.stopPropagation(); setNudge(false); };

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open, sending]);

  const submit = async (text) => {
    if (!text || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text }]);
    setSending(true);
    try {
      const res = await publicApi.chat({ session_id: sessionRef.current, message: text, page: window.location.pathname });
      if (res.session_id) { sessionRef.current = res.session_id; localStorage.setItem("cass_chat_session", res.session_id); }
      // Cassie captured a qualified lead — a genuine enquiry, so report it once
      // per session as its own conversion (SEO brief Workstream K).
      if (res.lead_captured && !sessionStorage.getItem("cass_chat_lead_tracked")) {
        sessionStorage.setItem("cass_chat_lead_tracked", "1");
        trackEvent("chat_lead_captured", { lead_quality: res.lead_quality || "Unknown" });
      }
      setMessages((m) => [...m, { role: "bot", text: res.reply, lets_talk: res.lets_talk, sources: res.sources || [] }]);
    } catch (err) {
      const msg = err?.response?.status === 429 ? "You're sending messages a little fast — please wait a moment." : "Sorry, something went wrong. Please try again.";
      setMessages((m) => [...m, { role: "bot", text: msg, lets_talk: true }]);
    } finally {
      setSending(false);
    }
  };

  const send = (e) => { e?.preventDefault(); submit(input.trim()); };

  // A link Cassie gave them, followed. Recorded so the mentions can be measured
  // against the follow-through.
  const followLink = (path) => {
    setOpen(false);
    publicApi.chatLinkClick({ path, session_id: sessionRef.current });
    trackEvent("chat_link_click", { link_path: path });
  };

  return (
    <>
      <button
        data-testid="chat-toggle"
        onClick={() => { if (open) { setOpen(false); } else { openChat("widget_button"); } setNudge(false); }}
        aria-label={open ? "Close chat" : "Chat with Cassie"}
        className={`fixed bottom-[4.75rem] right-4 sm:bottom-5 sm:right-5 z-[60] w-14 h-14 sm:w-16 sm:h-16 rounded-full shadow-xl flex items-center justify-center transition-all hover:-translate-y-0.5 overflow-hidden ${open ? "bg-orange hover:bg-orange-dark text-white shadow-orange/30" : "bg-white ring-2 ring-orange/60 shadow-navy/20"}`}
      >
        {open ? <X size={24} /> : <img loading="lazy" decoding="async" src={cassieAvatar} alt="Chat with Cassie" className="w-full h-full object-cover" />}
      </button>

      {nudge && !open && (
        <div
          data-testid="chat-nudge"
          onClick={() => openChat("nudge")}
          className="fixed bottom-[8.5rem] right-4 sm:bottom-24 sm:right-5 z-[60] max-w-[210px] sm:max-w-[260px] bg-white rounded-2xl rounded-br-sm shadow-2xl border border-slate-200 p-3 pr-8 sm:p-4 cursor-pointer animate-fade-up"
        >
          <button data-testid="chat-nudge-close" onClick={dismissNudge} aria-label="Dismiss" className="absolute top-2 right-2 text-slate-300 hover:text-slate-500"><X size={15} /></button>
          <div className="flex items-start gap-2.5">
            <img loading="lazy" decoding="async" src={cassieAvatar} alt="Cassie" className="w-8 h-8 rounded-full object-cover shrink-0" />
            <p className="text-sm text-navy leading-snug">Hi 👋🏻 I'm <span className="font-semibold">Cassie</span>, #teamCASS's AI Assistant. <span className="text-orange font-semibold">Ask me anything.</span></p>
          </div>
        </div>
      )}

      {open && (
        <div data-testid="chat-window" className="fixed bottom-24 right-5 z-[60] w-[calc(100vw-2.5rem)] sm:w-96 h-[540px] max-h-[75vh] bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden animate-fade-up">
          <div className="bg-navy text-white px-5 py-4 flex items-center gap-3 shrink-0">
            <img loading="lazy" decoding="async" src={cassieAvatar} alt="Cassie" className="w-9 h-9 rounded-full object-cover ring-2 ring-orange/50" />
            <div>
              <p className="font-display font-bold text-sm leading-tight">Cassie</p>
              <p className="text-[11px] text-slate-300 flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-green-400 inline-block" /> #teamCASS AI Assistant</p>
            </div>
          </div>

          <div ref={scrollRef} data-testid="chat-messages" className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-slate-50">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] ${m.role === "user" ? "bg-orange text-white rounded-2xl rounded-br-sm" : "bg-white text-navy border border-slate-100 rounded-2xl rounded-bl-sm"} px-4 py-2.5 text-sm leading-relaxed shadow-sm`}>
                  {m.role === "bot" ? (
                    <BotText text={m.text} onFollow={followLink} />
                  ) : (
                    <p className="whitespace-pre-wrap">{m.text}</p>
                  )}
                  {(m.sources || []).length > 0 && (
                    /* She answered from an approved answer: the visitor gets the
                       page it came from, so they can read it in full. */
                    <div className="mt-2.5 pt-2.5 border-t border-slate-100" data-testid={`chat-sources-${i}`}>
                      <p className="text-[10px] font-bold uppercase tracking-wide text-slate-400">From our answer library</p>
                      {m.sources.map((s) => (
                        <Link
                          key={s.slug}
                          to={s.url}
                          onClick={() => followLink(s.url)}
                          data-testid={`chat-source-${s.slug}`}
                          className="mt-1.5 flex items-start gap-1.5 text-xs font-semibold text-navy hover:text-orange transition-colors"
                        >
                          <ArrowRight size={12} className="shrink-0 mt-0.5 text-orange" />
                          <span>{s.question}</span>
                        </Link>
                      ))}
                    </div>
                  )}
                  {m.lets_talk && (
                    <Link to="/contact" data-testid="chat-lets-talk" onClick={() => setOpen(false)} className="mt-2.5 inline-flex items-center gap-1.5 bg-navy hover:bg-navy-deep text-white font-bold text-xs px-4 py-2 rounded-full transition-colors">
                      Let's Talk <ArrowRight size={13} />
                    </Link>
                  )}
                </div>
              </div>
            ))}
            {messages.length === 1 && !sending && suggestions.length > 0 && (
              <div className="pt-1" data-testid="chat-suggestions">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-2 px-1">{suggestionsHeading}</p>
                <div className="flex flex-wrap gap-2">
                  {suggestions.map((q, i) => (
                    <button
                      key={i}
                      type="button"
                      data-testid={`chat-suggestion-${i}`}
                      onClick={() => submit(q)}
                      className={`text-left text-xs font-semibold rounded-full border px-3.5 py-2 transition-all hover:-translate-y-0.5 bg-white ${isLC ? "border-teal/40 text-teal hover:bg-teal hover:text-white hover:border-teal" : "border-orange/40 text-orange hover:bg-orange hover:text-white hover:border-orange"}`}
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {sending && (
              <div className="flex justify-start">
                <div className="bg-white border border-slate-100 rounded-2xl rounded-bl-sm px-4 py-3 shadow-sm flex items-center gap-1.5">
                  <span className="w-2 h-2 bg-slate-300 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                  <span className="w-2 h-2 bg-slate-300 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                  <span className="w-2 h-2 bg-slate-300 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                </div>
              </div>
            )}
          </div>

          <p className="px-3 pt-2 text-[11px] leading-snug text-slate-400 bg-white" data-testid="chat-ai-notice">
            Cassie is an AI assistant and can make mistakes. Answers are general information, not advice for your
            circumstances. Search terms and chatbot messages are recorded to help answer questions and improve Ask
            Cassie — please don't enter passwords, bank details, National Insurance numbers, tax reference numbers or
            confidential information about another person or business.{" "}
            <Link to="/privacy-policy" className="underline hover:text-orange">Privacy Policy</Link>
          </p>
          <form onSubmit={send} className="p-3 border-t border-slate-100 bg-white flex items-center gap-2 shrink-0">
            <input
              data-testid="chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Type your question..."
              className="flex-1 rounded-full border border-slate-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange/40"
            />
            <button data-testid="chat-send" type="submit" disabled={sending || !input.trim()} className="w-10 h-10 rounded-full bg-orange hover:bg-orange-dark disabled:opacity-50 text-white flex items-center justify-center shrink-0 transition-colors">
              {sending ? <Loader2 size={17} className="animate-spin" /> : <Send size={17} />}
            </button>
          </form>
        </div>
      )}
    </>
  );
}
